/* Monument Letter Calculator — controller
 * --------------------------------------
 * Wires the snippet markup into a live state machine:
 *   - 6-step form (Letters, Size, Layout, Foam color, Finish, Details)
 *   - Live estimate with bump animation on every update
 *   - Step badges advance current → done as required fields fill
 *   - Layout=Custom reveals a notes textarea
 *   - Finish=Painted reveals the paint color picker
 *   - Submit POSTs JSON to the configured endpoint and swaps the body for
 *     the populated success card
 *
 * Pricing config is injected via window.monumentCalcConfig[sectionId] from
 * the snippet's hardcoded values.
 */

(function () {
  'use strict';

  function init(root) {
    var sectionId = root.getAttribute('data-section-id');
    var config = (window.monumentCalcConfig || {})[sectionId];
    if (!config) {
      console.warn('[MLC] Missing config for section', sectionId);
      return;
    }

    var els = {
      body: root.querySelector('#mlc-body'),
      form: root.querySelector('#mlc-form'),
      estimateCard: root.querySelector('#mlc-estimate-card'),
      estimateAmount: root.querySelector('#mlc-estimate-amount'),
      estimateEyebrow: root.querySelector('#mlc-estimate-eyebrow-text'),
      estimateIcon: root.querySelector('#mlc-estimate-icon'),
      estimateDisclaimer: root.querySelector('#mlc-estimate-disclaimer'),
      letters: root.querySelector('#mlc-letters'),
      lettersInput: root.querySelector('#mlc-letters-input'),
      lettersError: root.querySelector('#mlc-letters-error'),
      lettersErrorMsg: root.querySelector('#mlc-letters-error-msg'),
      charCurrent: root.querySelector('#mlc-char-current'),
      height: root.querySelector('#mlc-height'),
      depth: root.querySelector('#mlc-depth'),
      layoutNotesWrap: root.querySelector('#mlc-layout-notes-wrap'),
      layoutNotes: root.querySelector('#mlc-layout-notes'),
      paintPickerWrap: root.querySelector('#mlc-paint-picker-wrap'),
      formError: root.querySelector('#mlc-form-error'),
      formErrorMsg: root.querySelector('#mlc-form-error-msg'),
      submitBtn: root.querySelector('#mlc-submit-btn'),
      submitLabel: root.querySelector('.mlc__submit-label'),
      submitArrow: root.querySelector('.mlc__submit-arrow'),
      successTemplate: root.querySelector('#mlc-success-template'),
      honeypot: root.querySelector('#mlc-website'),
      name: root.querySelector('#mlc-name'),
      email: root.querySelector('#mlc-email'),
      phone: root.querySelector('#mlc-phone'),
      org: root.querySelector('#mlc-org'),
      installDate: root.querySelector('#mlc-install-date'),
      notes: root.querySelector('#mlc-notes'),
      notesCurrent: root.querySelector('#mlc-notes-current'),
      address1: root.querySelector('#mlc-address1'),
      address2: root.querySelector('#mlc-address2'),
      city: root.querySelector('#mlc-city'),
      province: root.querySelector('#mlc-province'),
      zip: root.querySelector('#mlc-zip'),
      country: root.querySelector('#mlc-country')
    };

    var state = {
      letters: '',
      height: null,
      depth: null,
      layout: null,
      layoutNotes: '',
      foamColor: null,
      finish: null,
      paintColor: null
    };

    var ALLOWED_RE = /[^A-Za-z0-9 ]/g;
    var VALID_LETTERS_RE = /^[A-Za-z0-9 ]{1,30}$/;

    // ============ Pricing ============

    function lookupIdx(arr, value) {
      for (var i = 0; i < arr.length; i++) {
        if (String(arr[i]).trim() === String(value).trim()) return i;
      }
      return -1;
    }

    function nonSpaceCharCount(s) {
      return (s || '').replace(/\s/g, '').length;
    }

    var BASE_PER_LETTER = 250;

    function computeEstimate(s, c) {
      var chars = nonSpaceCharCount(s.letters);
      if (!chars) {
        return { subtotal: 0, ready: false };
      }
      // Once letters are typed, always show an estimate. Unset fields use:
      //   - height: BASE_PER_LETTER ($250) flat — gives a starting baseline
      //     until a real height is picked
      //   - depth / foam: multiplier 1.0
      //   - finish: $0 cost
      var hi = s.height ? lookupIdx(c.heights, s.height) : -1;
      var di = s.depth ? lookupIdx(c.depths, s.depth) : -1;
      var fci = s.foamColor ? lookupIdx(c.foamColors, s.foamColor) : -1;
      var fi = s.finish ? lookupIdx(c.finishes, s.finish) : -1;

      var hp = (hi >= 0) ? Number(c.heightPrices[hi]) : BASE_PER_LETTER;
      var dm = (di >= 0) ? Number(c.depthMultipliers[di]) : 1;
      var fm = (fci >= 0) ? Number(c.foamColorMultipliers[fci]) : 1;
      var finishCost = (fi >= 0) ? Number(c.finishCosts[fi]) : 0;

      var subtotal = chars * hp * dm * fm + chars * finishCost;
      return { subtotal: subtotal, ready: true };
    }

    function fmtMoney(n) {
      return Math.round(n).toLocaleString('en-US');
    }

    // ============ Validation ============

    function isValidEmail(s) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(s || '').trim());
    }

    function stepDone(n) {
      switch (n) {
        case 1: return state.letters.length > 0 && VALID_LETTERS_RE.test(state.letters);
        case 2: return !!state.height && !!state.depth;
        case 3: {
          if (!state.layout) return false;
          if (state.layout === config.customLayoutValue && !state.layoutNotes.trim()) return false;
          return true;
        }
        case 4: return !!state.foamColor;
        case 5: {
          if (!state.finish) return false;
          if (state.finish === config.paintTriggerValue && !state.paintColor) return false;
          return true;
        }
        case 6: return !!els.name.value.trim() && isValidEmail(els.email.value);
        case 7: {
          return !!els.address1.value.trim() &&
                 !!els.city.value.trim() &&
                 !!els.province.value.trim() &&
                 !!els.zip.value.trim() &&
                 !!els.country.value.trim();
        }
      }
      return false;
    }

    function isFormValid() {
      for (var i = 1; i <= 7; i++) {
        if (!stepDone(i)) return false;
      }
      return true;
    }

    // ============ Render ============

    function renderEstimate() {
      var result = computeEstimate(state, config);
      if (!result.ready) {
        // Empty / starting state — show base "$250 / letter" and an explanatory hint
        els.estimateEyebrow.textContent = 'Starting at';
        els.estimateAmount.innerHTML =
          '<span class="currency">$</span><span>250</span><span class="per-unit">/ letter</span>';
        els.estimateIcon.hidden = true;
        els.estimateDisclaimer.textContent =
          'Your total will appear here as you configure your sign — final price confirmed by our team.';
      } else {
        els.estimateEyebrow.textContent = 'Estimated total';
        els.estimateAmount.innerHTML =
          '<span class="currency">$</span><span>' + fmtMoney(result.subtotal) + '</span>';
        els.estimateIcon.hidden = false;
        els.estimateDisclaimer.textContent =
          'Estimate — final price confirmed by our team before invoicing.';
        // Trigger the bump animation
        els.estimateCard.classList.remove('is-updating');
        void els.estimateCard.offsetWidth;
        els.estimateCard.classList.add('is-updating');
      }
    }

    function renderStepBadges() {
      // Find the lowest step that isn't done — that's "current". Earlier = done. Later = upcoming.
      var current = 8;
      for (var i = 1; i <= 7; i++) {
        if (!stepDone(i)) { current = i; break; }
      }
      for (var n = 1; n <= 7; n++) {
        var badge = root.querySelector('#mlc-badge-' + n);
        if (!badge) continue;
        badge.classList.remove('mlc__step-badge--done', 'mlc__step-badge--upcoming');
        if (n < current) {
          badge.classList.add('mlc__step-badge--done');
          badge.innerHTML =
            '<svg width="14" height="14" viewBox="0 0 14 14" fill="none">' +
            '<path d="M2 7.2L5.5 10.7L12 3.8" stroke="currentColor" stroke-width="2.2" ' +
            'stroke-linecap="round" stroke-linejoin="round"/></svg>';
        } else if (n > current) {
          badge.classList.add('mlc__step-badge--upcoming');
          badge.textContent = String(n);
        } else {
          badge.textContent = String(n);
        }
      }
    }

    function renderRadioActive(group) {
      // For a given radio group name, mark the parent label .is-active for the checked input
      var inputs = root.querySelectorAll('input[name="' + group + '"]');
      inputs.forEach(function (input) {
        var card = input.closest('.mlc__radio-card');
        if (!card) return;
        if (input.checked) {
          card.classList.add('is-active');
        } else {
          card.classList.remove('is-active');
        }
      });
    }

    function renderSubmitButton() {
      var valid = isFormValid();
      els.submitBtn.disabled = !valid;
      if (valid) {
        els.submitBtn.classList.remove('is-disabled');
      } else {
        els.submitBtn.classList.add('is-disabled');
      }
    }

    function rerender() {
      renderEstimate();
      renderStepBadges();
      renderSubmitButton();
      // The form-level error only appears in response to a submit attempt
      // with invalid fields. As soon as the user interacts again, hide it.
      if (els.formError) els.formError.hidden = true;
    }

    // ============ Field handlers ============

    function onLettersInput(e) {
      var raw = e.target.value || '';
      var sanitized = raw.replace(ALLOWED_RE, '').slice(0, 30).toUpperCase();
      if (sanitized !== raw) {
        e.target.value = sanitized;
      }
      state.letters = sanitized;
      els.charCurrent.textContent = sanitized.length;

      if (sanitized && !VALID_LETTERS_RE.test(sanitized)) {
        els.letters.classList.add('is-error');
        els.lettersError.hidden = false;
      } else {
        els.letters.classList.remove('is-error');
        els.lettersError.hidden = true;
      }
      rerender();
    }

    function onHeightChange() {
      state.height = els.height.value || null;
      rerender();
    }

    function onDepthChange() {
      state.depth = els.depth.value || null;
      rerender();
    }

    function onLayoutChange(e) {
      state.layout = e.target.value;
      renderRadioActive('mlc-layout');

      var isCustom = state.layout === config.customLayoutValue;
      if (isCustom) {
        els.layoutNotesWrap.hidden = false;
        els.layoutNotesWrap.classList.add('mlc__reveal');
      } else {
        els.layoutNotesWrap.hidden = true;
        els.layoutNotesWrap.classList.remove('mlc__reveal');
        state.layoutNotes = '';
        if (els.layoutNotes) els.layoutNotes.value = '';
      }
      rerender();
    }

    function onLayoutNotesInput(e) {
      state.layoutNotes = e.target.value || '';
      rerender();
    }

    function onFoamColorChange(e) {
      state.foamColor = e.target.value;
      renderRadioActive('mlc-foam-color');
      rerender();
    }

    function onFinishChange(e) {
      state.finish = e.target.value;
      renderRadioActive('mlc-finish');

      var isPainted = state.finish === config.paintTriggerValue;
      if (isPainted) {
        els.paintPickerWrap.hidden = false;
        els.paintPickerWrap.classList.add('mlc__reveal');
      } else {
        els.paintPickerWrap.hidden = true;
        els.paintPickerWrap.classList.remove('mlc__reveal');
        state.paintColor = null;
        var paintInputs = root.querySelectorAll('input[name="mlc-paint-color"]');
        paintInputs.forEach(function (i) { i.checked = false; });
        renderRadioActive('mlc-paint-color');
      }
      rerender();
    }

    function onPaintColorChange(e) {
      state.paintColor = e.target.value;
      renderRadioActive('mlc-paint-color');
      rerender();
    }

    function onLeadFieldInput() {
      rerender();
    }

    function onNotesInput() {
      els.notesCurrent.textContent = els.notes.value.length;
    }

    // ============ Submit ============

    function onSubmit(e) {
      e.preventDefault();
      els.formError.hidden = true;

      if (!isFormValid()) {
        els.formErrorMsg.textContent =
          'We need a couple more things before sending this off — see fields above.';
        els.formError.hidden = false;
        return;
      }

      // Honeypot — silently succeed for bots
      if (els.honeypot && els.honeypot.value) {
        showSuccess({ submittedAt: Date.now() });
        return;
      }

      if (!config.submitEndpoint) {
        els.formErrorMsg.textContent =
          'Submission endpoint not configured. Please contact support.';
        els.formError.hidden = false;
        return;
      }

      var result = computeEstimate(state, config);
      var payload = {
        letters: state.letters,
        height: state.height,
        depth: state.depth,
        layout: state.layout,
        layoutNotes: state.layoutNotes,
        foamColor: state.foamColor,
        finish: state.finish,
        paintColor: state.paintColor,
        estimatedPrice: Math.round(result.subtotal),
        characterCount: nonSpaceCharCount(state.letters),
        productHandle: config.productHandle,
        productTitle: config.productTitle,
        customer: {
          name: els.name.value.trim(),
          email: els.email.value.trim(),
          phone: els.phone.value.trim(),
          organization: els.org.value.trim(),
          installDate: els.installDate.value || null
        },
        shippingAddress: {
          address1: els.address1.value.trim(),
          address2: els.address2.value.trim(),
          city: els.city.value.trim(),
          province: els.province.value.trim(),
          zip: els.zip.value.trim(),
          country: els.country.value.trim()
        },
        notes: els.notes.value.trim(),
        website: els.honeypot ? els.honeypot.value : ''
      };

      // Loading state
      els.submitBtn.classList.add('is-loading');
      els.submitBtn.classList.remove('is-disabled');
      els.submitBtn.disabled = true;
      els.submitBtn.setAttribute('aria-busy', 'true');
      els.submitBtn.innerHTML =
        '<span class="mlc__spinner"></span><span>Sending…</span>';

      fetch(config.submitEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shop-Domain': window.location.hostname
        },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          return res.json().then(function (body) {
            return { ok: res.ok, status: res.status, body: body };
          });
        })
        .then(function (resp) {
          els.submitBtn.classList.remove('is-loading');
          els.submitBtn.removeAttribute('aria-busy');
          if (resp.ok && resp.body && resp.body.ok) {
            showSuccess({
              draftOrderName: resp.body.draftOrderName || null
            });
          } else if (resp.status === 429) {
            restoreSubmitButton();
            els.formErrorMsg.textContent =
              'Too many requests right now. Please try again in an hour or contact us directly.';
            els.formError.hidden = false;
          } else {
            restoreSubmitButton();
            els.formErrorMsg.textContent =
              (resp.body && resp.body.error)
                ? resp.body.error
                : 'Something went wrong. Please try again or email us at hello@popsigns.co.';
            els.formError.hidden = false;
          }
        })
        .catch(function (err) {
          console.error('[MLC] Submit failed:', err);
          els.submitBtn.classList.remove('is-loading');
          els.submitBtn.removeAttribute('aria-busy');
          restoreSubmitButton();
          els.formErrorMsg.textContent =
            'Network error — please check your connection and try again.';
          els.formError.hidden = false;
        });
    }

    function restoreSubmitButton() {
      els.submitBtn.disabled = false;
      els.submitBtn.innerHTML =
        '<span class="mlc__submit-label">Submit pricing request</span>' +
        '<svg class="mlc__submit-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none">' +
        '<path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" stroke-width="2" ' +
        'stroke-linecap="round" stroke-linejoin="round"/></svg>';
    }

    function showSuccess(info) {
      var template = els.successTemplate;
      if (!template || !template.content) {
        // Fallback: hide the form and show a simple message
        els.body.innerHTML =
          '<div class="mlc__success"><h3>Request received!</h3>' +
          '<p>Thanks — we\'ll email you a confirmed quote within 1 business day.</p></div>';
        return;
      }
      var clone = template.content.cloneNode(true);
      var recap = clone.querySelector('#mlc-success-recap');
      if (recap) {
        recap.innerHTML = buildRecapHtml(info);
      }
      // Replace the body contents with the success view
      els.body.innerHTML = '';
      els.body.appendChild(clone);
      // Move focus to the heading for accessibility
      var heading = els.body.querySelector('h3');
      if (heading) heading.focus();
    }

    function buildRecapHtml(info) {
      var parts = [];
      parts.push('<div><dt>Letters</dt> <dd>' + escapeHtml(state.letters) + '</dd></div>');
      parts.push('<div><dt>Size</dt> <dd>' + escapeHtml(state.height + ' tall · ' + state.depth + ' deep') + '</dd></div>');
      parts.push('<div><dt>Layout</dt> <dd>' + escapeHtml(state.layout) + '</dd></div>');
      var finishText = state.finish;
      if (state.paintColor) finishText += ' · ' + state.paintColor;
      parts.push('<div><dt>Finish</dt> <dd>' + escapeHtml(finishText) + '</dd></div>');
      if (info && info.draftOrderName) {
        parts.push('<div><dt>Reference</dt> <dd>' + escapeHtml(info.draftOrderName) + '</dd></div>');
      }
      return parts.join('');
    }

    function escapeHtml(s) {
      var div = document.createElement('div');
      div.textContent = s == null ? '' : String(s);
      return div.innerHTML;
    }

    // ============ Wire up ============

    els.lettersInput.addEventListener('input', onLettersInput);
    els.height.addEventListener('change', onHeightChange);
    els.depth.addEventListener('change', onDepthChange);

    root.querySelectorAll('input[name="mlc-layout"]').forEach(function (i) {
      i.addEventListener('change', onLayoutChange);
    });
    if (els.layoutNotes) {
      els.layoutNotes.addEventListener('input', onLayoutNotesInput);
    }

    root.querySelectorAll('input[name="mlc-foam-color"]').forEach(function (i) {
      i.addEventListener('change', onFoamColorChange);
    });
    root.querySelectorAll('input[name="mlc-finish"]').forEach(function (i) {
      i.addEventListener('change', onFinishChange);
    });
    root.querySelectorAll('input[name="mlc-paint-color"]').forEach(function (i) {
      i.addEventListener('change', onPaintColorChange);
    });

    [
      els.name, els.email, els.phone, els.org, els.installDate, els.notes,
      els.address1, els.address2, els.city, els.province, els.zip, els.country
    ].forEach(function (el) {
      if (el) el.addEventListener('input', onLeadFieldInput);
    });
    if (els.notes) els.notes.addEventListener('input', onNotesInput);

    els.form.addEventListener('submit', onSubmit);

    // Initial render
    rerender();

    // JS-based sticky for the estimate card. CSS `position: sticky` is
    // unreliable because the Shopify theme has `body { overflow-x: hidden }`
    // and `.shopify-section + .shopify-section { content-visibility: auto }`
    // which together can prevent sticky from engaging. Plain `position: fixed`
    // with a placeholder is bulletproof.
    setupSticky();

    function setupSticky() {
      var card = els.estimateCard;
      if (!card || !card.parentElement) return;

      var STICKY_TOP = 90;
      var placeholder = null;
      var stuck = false;
      var rafId = 0;

      function applyStuck() {
        var rect = card.getBoundingClientRect();
        placeholder = document.createElement('div');
        placeholder.style.height = card.offsetHeight + 'px';
        placeholder.style.marginBottom = window.getComputedStyle(card).marginBottom;
        placeholder.setAttribute('aria-hidden', 'true');
        // Defeat `div:empty { display: none }` from base.css — without this
        // the placeholder collapses to 0 height and the page layout shifts.
        placeholder.textContent = '​';
        card.parentNode.insertBefore(placeholder, card);

        card.classList.add('is-stuck');
        card.style.position = 'fixed';
        card.style.top = STICKY_TOP + 'px';
        card.style.left = rect.left + 'px';
        card.style.width = rect.width + 'px';
        card.style.zIndex = '100';
        stuck = true;
      }

      function releaseStuck() {
        card.classList.remove('is-stuck');
        card.style.position = '';
        card.style.top = '';
        card.style.left = '';
        card.style.width = '';
        card.style.zIndex = '';
        if (placeholder && placeholder.parentNode) {
          placeholder.parentNode.removeChild(placeholder);
        }
        placeholder = null;
        stuck = false;
      }

      function refreshStuckGeometry() {
        if (!stuck || !placeholder) return;
        var phRect = placeholder.getBoundingClientRect();
        card.style.left = phRect.left + 'px';
        card.style.width = phRect.width + 'px';
      }

      function update() {
        rafId = 0;
        // The "natural" top of the card (placeholder when stuck, card itself when free)
        var ref = stuck ? placeholder : card;
        if (!ref) return;
        var refTop = ref.getBoundingClientRect().top;

        if (!stuck && refTop <= STICKY_TOP) {
          applyStuck();
        } else if (stuck && refTop > STICKY_TOP) {
          releaseStuck();
        }
      }

      function schedule() {
        if (rafId) return;
        rafId = window.requestAnimationFrame(update);
      }

      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', function () {
        refreshStuckGeometry();
        schedule();
      }, { passive: true });
      // Initial check (in case the user is mid-scroll on page load)
      schedule();
    }
  }

  function bootstrap() {
    var roots = document.querySelectorAll('.mlc[data-section-id]');
    roots.forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('.mlc[data-section-id]');
    if (root) init(root);
  });
})();
